import { LinkAccountForm } from '@pulse/auth-ui';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Account-linking page mounted at `/auth/link-account?flow=<id>`. The user reaches it from
 * `<SocialCallback>` when an OIDC sign-in's email matches an existing local identity.
 *
 * After a successful link, navigate to whatever the original next-path was (the social
 * button parked it in localStorage; `<LinkAccountForm>` doesn't read it directly, so we
 * keep the navigation logic here and default to '/').
 */
export function LinkAccountPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow') ?? '';

  if (flowId.length === 0) {
    return (
      <div className="grid min-h-screen place-items-center bg-muted/20 px-4">
        <p className="text-sm text-muted-foreground">Missing flow id. Return to <a className="auth-ui-link" href="/login">sign in</a>.</p>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen place-items-center bg-muted/20 px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Link sign-in method</h1>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Confirm it&apos;s you</CardTitle>
            <CardDescription>
              Use your existing password or get a one-time code by email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LinkAccountForm
              flowId={flowId}
              onSuccess={() => navigate('/', { replace: true })}
              onNotLinkable={() => navigate('/login', { replace: true })}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
